import React from 'react';
import { CheckIcon } from 'src/ui/icons';
import type { LabelStyle } from './types';
import * as S from './styles';

export const CheckboxInput = ({
  name,
  onChange,
  checked,
  partial,
  tabIndex,
  disabled,
  required,
}: CheckboxInputProps) => (
  <>
    <S.Input
      required={required}
      id={name}
      name={name}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      tabIndex={tabIndex}
      value={checked ? '1' : '0'}
    />

    <S.CheckIconWrapper checked={checked} partial={partial}>
      <CheckIcon checked={checked} partial={partial} height={14} />
    </S.CheckIconWrapper>
  </>
);

type CheckboxInputProps = {
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  checked?: boolean;
  partial?: boolean;
  tabIndex?: number;
  disabled?: boolean;
  required?: boolean;
};

CheckboxInput.defaultProps = {
  name: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: () => {},
  checked: false,
  partial: false,
  tabIndex: '0',
  disabled: false,
  required: false,
};

const Checkbox = ({
  name,
  checked,
  partial,
  helper,
  warning,
  label,
  labelStyle = 'normal',
  onChange,
  className,
  tabIndex,
  disabled,
  children,
  required,
  ...props
}: CheckboxProps) => {
  const labelText = children || label;

  return (
    <S.Checkbox
      className={className}
      disabled={disabled}
      data-test={props['data-test']}
    >
      <S.Label htmlFor={name} disabled={disabled}>
        <CheckboxInput
          name={name}
          onChange={onChange}
          checked={checked}
          partial={partial}
          tabIndex={tabIndex}
          disabled={disabled}
          required={required}
        />

        <S.LabelText labelStyle={labelStyle}>
          <span>{labelText}</span>
          {helper && <S.Helper warning={warning}>{helper}</S.Helper>}
        </S.LabelText>
      </S.Label>
    </S.Checkbox>
  );
};

type CheckboxProps = {
  name: string;
  onChange: ((event: React.ChangeEvent<HTMLInputElement>) => void) | undefined;
  label?: string;
  labelStyle?: LabelStyle;
  checked?: boolean;
  partial?: boolean;
  helper?: string;
  warning?: boolean;
  className?: string;
  tabIndex?: number;
  disabled?: boolean;
  children?: React.ReactNode;
  required?: boolean;
  'data-test'?: string;
};

Checkbox.defaultProps = {
  label: null,
  labelStyle: 'normal',
  checked: false,
  partial: false,
  helper: null,
  warning: false,
  className: null,
  tabIndex: '0',
  disabled: false,
  children: null,
  required: false,
  'data-test': undefined,
};

export default Checkbox;
